import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditBasic } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "cc-frontend/lib/action-doc-builder"
import { assign, partial } from "lodash-es"
import type { Narrative } from "../create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  value: string | null
  originalValue: string | null
  cardStackId: string
}

export default function editLessonTitleAction(payload: Payload): PreparedAction {
  return {
    name: "LESSON_EDIT_TITLE",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload) {
  let patch = Patch.create("card-stack", payload.cardStackId).set(
    "attributes.title",
    payload.value
  ).patch

  let patchForCardStackSummary = Patch.create("card-stack-summary", payload.cardStackId).set(
    "attributes.title",
    payload.value
  ).patch

  return [patchForCardStackSummary, patch]
}

function undoPatches(payload: Payload) {
  let patch = Patch.create("card-stack", payload.cardStackId).set(
    "attributes.title",
    payload.originalValue
  ).patch

  let patchForCardStackSummary = Patch.create("card-stack-summary", payload.cardStackId).set(
    "attributes.title",
    payload.originalValue
  ).patch

  return [patchForCardStackSummary, patch]
}

function narrative(payload: Payload, findFn: FindFn, userId): Narrative {
  let models = defaultLessonModels(payload, findFn, userId)
  let context = defaultLessonContext(payload, models)
  let newContext = assign({}, context, {
    oldTitle: payload.originalValue,
    newTitle: payload.value,
  })
  let newEvent: LessonEditBasic = {
    ...createBlankEvent(),
    root_id: models.planbook?.id ?? null,
    root_type: "planbook",
    date_plan_scheduled_for: models.dateStringForCardStack,
    primary_object_id: payload.cardStackId,
    primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,

    primary_verb: "edit",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
    primary_object_type: "lesson",
    primary_object_specifier: "title",

    adverbial_verb: null,
    adverbial_object_type: null,
    adverbial_object_specifier: null,
    adverbial_object_id: null,
    adverbial_possessive: null,
    adverbial_object_id_and_ancestor_ids: null,
  }

  return {
    title: "Renamed Lesson",
    titleForAnalytics: "Lesson - Edit Title",
    description: `Changed the title from "${payload.originalValue}" to "${payload.value}"`,
    activity: defaultLessonActivity(payload, models),
    context: newContext,
    newAnalyticsEvents: [newEvent],
  }
}
